import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Eye, EyeOff } from 'lucide-react';
import LoginLayout from './LoginLayout';
import { MIN_PASSWORD_LENGTH } from '../api/passwordResetApi';
import { useResetPassword } from '../hooks/auth/useResetPassword';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { NEW_LINK_PARAM } from './loginPageParams';

/**
 * Set-password page the reset and welcome emails link to. It lives on the
 * customer's own domain, next to the login page, so a password the browser
 * saves here fills in when logging in (molnfast-react#1783).
 */
const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { state, save } = useResetPassword(searchParams.get('oobCode'));
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useDocumentTitle('Välj lösenord – Molnfastigheter');

    useEffect(() => {
        if (state.step !== 'done') return;
        if (state.outcome === 'signedIn') {
            navigate('/', { replace: true });
        } else if (state.outcome === 'signInFailed') {
            // The password is saved; logging in by hand still works
            navigate('/signIn', { replace: true, state: { email: state.email } });
        }
    }, [state, navigate]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void save(password);
    };

    const renderContent = () => {
        switch (state.step) {
            case 'checking':
                return <p className="login-message">Kontrollerar länken…</p>;

            case 'badLink':
                return (
                    <div className="login-form">
                        <p className="login-message">Länken har slutat gälla eller har redan använts.</p>
                        <Button
                            variant="outline-light"
                            className="mx-auto block mt-3 text-sm"
                            style={{ borderWidth: '2px' }}
                            onClick={() => navigate(`/signIn?${NEW_LINK_PARAM}=1`, { replace: true })}
                        >
                            Beställ en ny länk
                        </Button>
                    </div>
                );

            case 'loadError':
                return (
                    <div className="login-form">
                        <div className="error-message">{state.message}</div>
                    </div>
                );

            case 'done':
                if (state.outcome !== 'appOnly') return <p className="login-message">Loggar in…</p>;
                return (
                    <div className="login-form">
                        <p className="login-message">
                            Lösenordet är sparat. Logga in i appen Molnfastigheter på din iPhone.
                        </p>
                    </div>
                );

            case 'form':
                return (
                    <form onSubmit={handleSubmit} className="login-form">
                        {state.error && <div className="error-message">{state.error}</div>}

                        <span className="field-title">E-postadress</span>
                        <input
                            type="email"
                            name="email"
                            value={state.email}
                            autoComplete="username"
                            readOnly
                            className="input-field input-field--readonly"
                        />

                        <span className="field-title">Nytt lösenord</span>
                        <div className="password-field">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={MIN_PASSWORD_LENGTH}
                                required
                                autoFocus
                                disabled={state.saving}
                                className="input-field"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword((shown) => !shown)}
                                aria-label={showPassword ? 'Dölj lösenordet' : 'Visa lösenordet'}
                                aria-pressed={showPassword}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <span className="field-hint">Minst {MIN_PASSWORD_LENGTH} tecken</span>

                        <Button
                            variant="outline-light"
                            className="mx-auto block mt-5 text-sm"
                            style={{ borderWidth: '2px' }}
                            type="submit"
                            disabled={state.saving}
                        >
                            {state.saving ? 'Sparar…' : 'Spara lösenord'}
                        </Button>
                    </form>
                );
        }
    };

    return <LoginLayout>{renderContent()}</LoginLayout>;
};

export default ResetPasswordPage;
